import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot, Trash2, ChevronDown, ChevronUp, Package, Bell, Mail, Search, Loader2, Send, ExternalLink, Check, AlertTriangle, Edit3, ShieldCheck, Globe, Copy, RefreshCw, ScanLine } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PublishControl = ({ scan, onUpdate }) => {
  const [working, setWorking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [reanalysing, setReanalysing] = useState(false);
  const liveUrl = scan.slug ? `${window.location.origin}/vs/${scan.slug}` : null;

  const toggle = async (e) => {
    e.stopPropagation();
    setWorking(true);
    try {
      const res = await axios.post(
        `${API}/admin/ted-ai-scans/${scan.id}/publish`,
        { published: !scan.published },
        { headers: authHeaders() }
      );
      onUpdate(res.data);
      toast.success(res.data.published ? 'Page is now live' : 'Page unpublished');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update');
    } finally {
      setWorking(false);
    }
  };

  const regenerate = async (e) => {
    e.stopPropagation();
    setRegenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/ted-ai-scans/${scan.id}/regenerate-comparison`,
        {},
        { headers: authHeaders() }
      );
      onUpdate(res.data);
      toast.success('Comparison regenerated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Regenerate failed');
    } finally {
      setRegenerating(false);
    }
  };

  const reanalyse = async (e) => {
    e.stopPropagation();
    setReanalysing(true);
    try {
      const res = await axios.post(
        `${API}/admin/ted-ai-scans/${scan.id}/reanalyze`,
        {},
        { headers: authHeaders() }
      );
      onUpdate(res.data);
      toast.success('Bag re-analysed — nutrition data refreshed. Click regenerate next to refresh the comparison.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Re-analyse failed');
    } finally {
      setReanalysing(false);
    }
  };

  const copy = (e) => {
    e.stopPropagation();
    if (!liveUrl) return;
    navigator.clipboard?.writeText(liveUrl);
    toast.success('Link copied');
  };

  return (
    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
      <label className="flex items-center gap-1.5 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={!!scan.published}
          onChange={toggle}
          disabled={working}
          className="w-4 h-4 accent-[#0FA49C]"
          data-testid={`publish-toggle-${scan.id}`}
        />
        <span className={`text-xs font-bold uppercase tracking-wider flex items-center gap-1 ${
          scan.published ? 'text-[#0FA49C]' : 'text-[#999]'
        }`}>
          <Globe className="w-3 h-3" />
          {working ? '…' : scan.published ? 'Live' : 'Publish'}
        </span>
      </label>
      {scan.published && (
        <>
          <button
            onClick={reanalyse}
            title="Re-read the original bag images and refresh nutrition / ingredients data (use if Guaranteed Analysis is empty)"
            disabled={reanalysing}
            className="text-[#0FA49C] hover:bg-[#0FA49C]/10 rounded p-1 disabled:opacity-50"
            data-testid={`publish-reanalyse-${scan.id}`}
          >
            {reanalysing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ScanLine className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={regenerate}
            title="Regenerate AI comparison (verdict, ingredients, FAQs)"
            disabled={regenerating}
            className="text-[#0FA49C] hover:bg-[#0FA49C]/10 rounded p-1 disabled:opacity-50"
            data-testid={`publish-regen-${scan.id}`}
          >
            {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
          {liveUrl && (
            <>
              <button
                onClick={copy}
                title="Copy public URL"
                className="text-[#0FA49C] hover:bg-[#0FA49C]/10 rounded p-1"
                data-testid={`publish-copy-${scan.id}`}
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
              <a
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
                title="Open public page"
                className="text-[#0FA49C] hover:bg-[#0FA49C]/10 rounded p-1"
                data-testid={`publish-open-${scan.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </>
          )}
        </>
      )}
    </div>
  );
};

const ScanCard = ({ scan, onDelete, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const bag = scan.scanned_bag;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`scan-${scan.id}`}>
      {/* Header row */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-[#0FA49C]/10 flex items-center justify-center flex-shrink-0">
            <Bot className="w-5 h-5 text-[#0FA49C]" />
          </div>
          <div className="min-w-0">
            <p className="font-bold text-[#222] text-sm truncate">
              {bag.brand} — {bag.product_name}
            </p>
            <p className="text-xs text-[#999]">
              {new Date(scan.created_at).toLocaleString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })}
              {scan.email && <span className="ml-2 text-blue-600">· {scan.email}</span>}
              {bag.protein_source && <span className="ml-2 text-[#666]">· {bag.protein_source}</span>}
              {bag.type && <span className="ml-2 text-[#666]">· {bag.type.replace(/_/g, ' ')}</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          <PublishControl scan={scan} onUpdate={onUpdate} />
          <span className="text-xs font-medium text-[#666] bg-gray-100 px-2 py-1 rounded-full">
            {scan.recommended_products.length} match{scan.recommended_products.length !== 1 ? 'es' : ''}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
        </div>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {/* Scanned bag info */}
              <div>
                <p className="text-xs font-bold text-[#999] uppercase tracking-wide mb-2">Scanned Bag Details</p>

                {/* Bag images */}
                {(scan.front_image_url || scan.back_image_url) && (
                  <div className="flex gap-3 mb-3">
                    {scan.front_image_url && (
                      <div className="w-28 h-36 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
                        <img src={`${BACKEND_URL}${scan.front_image_url}`} alt="Front of bag" className="w-full h-full object-cover" />
                      </div>
                    )}
                    {scan.back_image_url && (
                      <div className="w-28 h-36 rounded-xl overflow-hidden border border-gray-200 flex-shrink-0">
                        <img src={`${BACKEND_URL}${scan.back_image_url}`} alt="Back of bag" className="w-full h-full object-cover" />
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {bag.brand && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Brand</p>
                      <p className="text-sm font-semibold text-[#222]">{bag.brand}</p>
                    </div>
                  )}
                  {bag.product_name && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Product</p>
                      <p className="text-sm font-semibold text-[#222]">{bag.product_name}</p>
                    </div>
                  )}
                  {bag.protein_source && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Protein</p>
                      <p className="text-sm font-semibold text-[#222]">{bag.protein_source}</p>
                    </div>
                  )}
                  {bag.type && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Type</p>
                      <p className="text-sm font-semibold text-[#222] capitalize">{bag.type.replace(/_/g, ' ')}</p>
                    </div>
                  )}
                  {bag.lifestage && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Lifestage</p>
                      <p className="text-sm font-semibold text-[#222] capitalize">{bag.lifestage}</p>
                    </div>
                  )}
                  {bag.breed_size && (
                    <div className="bg-gray-50 rounded-lg p-2.5">
                      <p className="text-[10px] text-[#999] uppercase">Breed Size</p>
                      <p className="text-sm font-semibold text-[#222] capitalize">{bag.breed_size}</p>
                    </div>
                  )}
                  {bag.special_needs && (
                    <div className="bg-gray-50 rounded-lg p-2.5 col-span-2">
                      <p className="text-[10px] text-[#999] uppercase">Special Needs</p>
                      <p className="text-sm font-semibold text-[#222]">{bag.special_needs}</p>
                    </div>
                  )}
                </div>
                {bag.ingredients && (
                  <div className="bg-gray-50 rounded-lg p-2.5 mt-2">
                    <p className="text-[10px] text-[#999] uppercase">Ingredients</p>
                    <p className="text-xs text-[#444] leading-relaxed mt-0.5">{bag.ingredients}</p>
                  </div>
                )}
              </div>

              {/* Recommended products */}
              <div>
                <p className="text-xs font-bold text-[#999] uppercase tracking-wide mb-2">Recommended Products</p>
                <div className="space-y-2">
                  {scan.recommended_products.map((rec, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl p-3 flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0FA49C] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {rec.match_score}%
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-[#222] text-sm">{rec.product_name}</p>
                        {rec.why_selected && (
                          <p className="text-xs text-[#666] mt-1"><span className="font-semibold text-[#0FA49C]">Why:</span> {rec.why_selected}</p>
                        )}
                        {rec.how_its_better && (
                          <p className="text-xs text-[#666] mt-1"><span className="font-semibold text-green-600">Better:</span> {rec.how_its_better}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Explanation */}
              {scan.explanation && (
                <div className="bg-[#0FA49C]/5 border border-[#0FA49C]/20 rounded-xl p-3">
                  <p className="text-xs text-[#444] leading-relaxed">{scan.explanation}</p>
                </div>
              )}

              {/* Delete */}
              <div className="flex justify-end">
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(scan.id); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-scan-${scan.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete Scan
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const PriceAlertCard = ({ alert, onDelete, onUpdate, getAuthHeader, priceComparisonEnabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editPrices, setEditPrices] = useState([]);
  const [saving, setSaving] = useState(false);

  const handleSearch = async (e) => {
    e.stopPropagation();
    setSearching(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.post(`${API}/admin/price-alerts/${alert.id}/search`, {}, { headers });
      if (response.data.status === 'success') {
        onUpdate({
          id: alert.id,
          search_results: response.data.search_results,
          cheapest_price: response.data.cheapest_price,
          cheapest_site: response.data.cheapest_site,
          cheapest_url: response.data.cheapest_url,
          summary: response.data.summary,
          searched_at: response.data.searched_at,
          verified: false,
        });
        setExpanded(true);
        toast.success('Price search complete — please verify before sending.');
      } else {
        toast.error(response.data.message || 'No results found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed. Try again.');
    } finally {
      setSearching(false);
    }
  };

  const handleStartEdit = (e) => {
    e.stopPropagation();
    setEditPrices((alert.search_results || []).map(r => ({ ...r })));
    setEditing(true);
  };

  const handleEditPrice = (index, field, value) => {
    setEditPrices(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: field === 'price' ? parseFloat(value) || 0 : value };
      return updated;
    });
  };

  const handleRemoveRow = (index) => {
    setEditPrices(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveVerified = async (e) => {
    e.stopPropagation();
    setSaving(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const validPrices = editPrices.filter(r => r.price > 0 && r.site_name);
      const cheapest = validPrices.reduce((min, r) => r.price < min.price ? r : min, validPrices[0] || {});

      await axios.put(`${API}/admin/price-alerts/${alert.id}/verify`, {
        search_results: validPrices,
        cheapest_price: cheapest.price || null,
        cheapest_site: cheapest.site_name || null,
        cheapest_url: cheapest.url || null,
      }, { headers });

      onUpdate({
        id: alert.id,
        search_results: validPrices,
        cheapest_price: cheapest.price,
        cheapest_site: cheapest.site_name,
        cheapest_url: cheapest.url,
        verified: true,
      });
      setEditing(false);
      toast.success('Prices verified and saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleNotify = async (e) => {
    e.stopPropagation();
    setSending(true);
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.post(`${API}/admin/price-alerts/${alert.id}/notify`, {}, { headers });
      if (response.data.status === 'success') {
        onUpdate({ id: alert.id, notified_at: new Date().toISOString() });
        toast.success(`Email sent to ${alert.email}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const hasResults = alert.search_results && alert.search_results.length > 0;
  const isCheaper = hasResults && alert.current_price && alert.cheapest_price < alert.current_price;
  const isOwnBrand = (alert.brand || '').toLowerCase().includes('ultimate pet');
  const isVerified = alert.verified === true;

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid={`alert-${alert.id}`}>
      {/* Header */}
      <div
        className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4 min-w-0 flex-1">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
            isOwnBrand ? 'bg-[#0FA49C]/10' : hasResults ? 'bg-green-100' : 'bg-amber-100'
          }`}>
            {isOwnBrand ? <Check className="w-5 h-5 text-[#0FA49C]" /> : hasResults ? <Check className="w-5 h-5 text-green-600" /> : <Bell className="w-5 h-5 text-amber-600" />}
          </div>
          <div className="min-w-0 flex-1">
            <p className="font-bold text-[#222] text-sm truncate">
              {alert.brand} — {alert.product_name}
              {alert.bag_weight && <span className="text-[#999] font-normal"> · {alert.bag_weight}</span>}
            </p>
            <p className="text-xs text-[#999] flex flex-wrap items-center gap-x-2">
              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{alert.email}</span>
              {alert.current_price != null && <span>· Pays £{Number(alert.current_price).toFixed(2)}</span>}
              {alert.dog_name && <span>· {alert.dog_name}</span>}
              {isOwnBrand && <span className="text-[#0FA49C] font-semibold">· Our product</span>}
              {!isOwnBrand && hasResults && (
                <span className={`font-semibold ${isCheaper ? 'text-green-600' : 'text-[#666]'}`}>
                  · Found £{Number(alert.cheapest_price).toFixed(2)} at {alert.cheapest_site}
                </span>
              )}
              {!isOwnBrand && hasResults && !isVerified && (
                <span className="text-amber-600 font-semibold flex items-center gap-0.5">· <AlertTriangle className="w-3 h-3" /> Unverified</span>
              )}
              {!isOwnBrand && hasResults && isVerified && (
                <span className="text-green-600 font-semibold flex items-center gap-0.5">· <ShieldCheck className="w-3 h-3" /> Verified</span>
              )}
              {alert.notified_at && <span className="text-green-600 font-medium">· Sent</span>}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {!isOwnBrand && priceComparisonEnabled && (
            <>
            <Button
              size="sm"
              variant={hasResults ? "outline" : "default"}
              onClick={handleSearch}
              disabled={searching}
              className={`text-xs h-8 ${!hasResults ? 'bg-[#0FA49C] hover:bg-[#0d8f88] text-white' : ''}`}
              data-testid={`search-alert-${alert.id}`}
            >
            {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
            {searching ? 'Searching...' : hasResults ? 'Re-search' : 'Search Price'}
          </Button>
          {hasResults && !isVerified && (
            <Button
              size="sm"
              onClick={handleStartEdit}
              className="text-xs h-8 bg-amber-500 hover:bg-amber-600 text-white"
              data-testid={`verify-alert-${alert.id}`}
            >
              <Edit3 className="w-3.5 h-3.5 mr-1" />
              Verify Prices
            </Button>
          )}
          {hasResults && isVerified && (
            <Button
              size="sm"
              onClick={handleNotify}
              disabled={sending}
              className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`notify-alert-${alert.id}`}
            >
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
              {alert.notified_at ? 'Resend' : 'Send to Customer'}
            </Button>
          )}
          </>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[#999]" /> : <ChevronDown className="w-4 h-4 text-[#999]" />}
        </div>
      </div>

      {/* Expanded results */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 border-t border-gray-100 pt-4 space-y-4">
              {/* Own brand message */}
              {isOwnBrand && (
                <div className="bg-[#0FA49C]/5 border border-[#0FA49C]/20 rounded-xl p-4 text-center">
                  <p className="text-sm font-semibold text-[#0FA49C] mb-1">This is an Ultimate Pet Foods product</p>
                  <p className="text-xs text-[#666]">Price comparison is not available for our own products. This customer is already buying from us!</p>
                </div>
              )}

              {/* Summary */}
              {!isOwnBrand && alert.summary && (
                <div className="bg-gray-50 rounded-xl p-3">
                  <p className="text-sm text-[#444]">{alert.summary}</p>
                  {alert.searched_at && (
                    <p className="text-xs text-[#999] mt-1">
                      Searched: {new Date(alert.searched_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
              )}

              {/* Saving banner */}
              {!isOwnBrand && isCheaper && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                  <p className="text-xs text-green-700 font-medium">Potential Saving</p>
                  <p className="text-2xl font-extrabold text-green-700">
                    £{(alert.current_price - alert.cheapest_price).toFixed(2)}
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    Cheapest: £{Number(alert.cheapest_price).toFixed(2)} at {alert.cheapest_site}
                  </p>
                  <p className="text-[10px] text-[#999] mt-2 italic">
                    Source: Prices sourced via AI (GPT-4o) based on UK retailer knowledge. Prices may not reflect live pricing — always verify with the retailer.
                  </p>
                </div>
              )}

              {/* Search results table */}
              {hasResults && !editing && (
                <div className="border rounded-xl overflow-hidden">
                  {!isVerified && (
                    <div className="bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                      <p className="text-xs text-amber-700 font-medium">AI-estimated prices — please verify before sending to customer</p>
                    </div>
                  )}
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[#666] uppercase">Retailer</th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium text-[#666] uppercase">Notes</th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium text-[#666] uppercase">Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {alert.search_results.map((r, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-4 py-2.5">
                            <a
                              href={r.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-[#0FA49C] hover:underline font-semibold flex items-center gap-1"
                            >
                              {r.site_name}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-4 py-2.5 text-xs text-[#666] max-w-[200px] truncate">{r.notes || '—'}</td>
                          <td className="px-4 py-2.5 text-right">
                            <span className={`text-sm font-bold ${
                              alert.current_price && r.price < alert.current_price ? 'text-green-600' : 'text-[#222]'
                            }`}>
                              £{Number(r.price).toFixed(2)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Editing mode */}
              {editing && (
                <div className="border-2 border-amber-300 rounded-xl overflow-hidden bg-amber-50/30">
                  <div className="bg-amber-100 px-4 py-2.5 flex items-center justify-between">
                    <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5">
                      <Edit3 className="w-3.5 h-3.5" />
                      Edit & verify prices — correct any inaccuracies before sending
                    </p>
                  </div>
                  <div className="p-3 space-y-2">
                    {editPrices.map((r, i) => (
                      <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 flex items-center gap-3">
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <input
                            value={r.site_name}
                            onChange={(e) => handleEditPrice(i, 'site_name', e.target.value)}
                            className="w-full text-sm font-semibold text-[#222] border-b border-transparent hover:border-gray-300 focus:border-[#0FA49C] focus:outline-none pb-0.5"
                            placeholder="Retailer name"
                          />
                          <input
                            value={r.url || ''}
                            onChange={(e) => handleEditPrice(i, 'url', e.target.value)}
                            className="w-full text-xs text-[#999] border-b border-transparent hover:border-gray-300 focus:border-[#0FA49C] focus:outline-none pb-0.5"
                            placeholder="URL"
                          />
                          <input
                            value={r.notes || ''}
                            onChange={(e) => handleEditPrice(i, 'notes', e.target.value)}
                            className="w-full text-xs text-[#666] border-b border-transparent hover:border-gray-300 focus:border-[#0FA49C] focus:outline-none pb-0.5"
                            placeholder="Notes (bag size, delivery etc.)"
                          />
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-medium">£</span>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={r.price}
                              onChange={(e) => handleEditPrice(i, 'price', e.target.value)}
                              className="w-24 pl-6 pr-2 py-1.5 text-sm font-bold text-[#222] border rounded-lg focus:ring-2 focus:ring-[#0FA49C] focus:outline-none"
                            />
                          </div>
                          <button
                            onClick={() => handleRemoveRow(i)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="px-3 pb-3 flex items-center gap-2 justify-end">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setEditing(false); }}
                      className="text-xs h-8"
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSaveVerified}
                      disabled={saving || editPrices.length === 0}
                      className="text-xs h-8 bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`save-verified-${alert.id}`}
                    >
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                      Verify & Save
                    </Button>
                  </div>
                </div>
              )}

              {!hasResults && (
                <div className="text-center py-6 text-[#999] text-sm">
                  No search results yet. Click "Search Price" to find prices online.
                </div>
              )}

              {/* Footer actions */}
              <div className="flex justify-between items-center pt-2">
                <p className="text-xs text-[#999]">
                  Created: {new Date(alert.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                </p>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(alert.id); }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  data-testid={`delete-alert-${alert.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


export const TedAIScans = () => {
  const [scans, setScans] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceComparisonEnabled, setPriceComparisonEnabled] = useState(true);

  const getAuthHeader = () => {
    const token = localStorage.getItem('admin_token');
    return token ? { Authorization: `Bearer ${token}` } : null;
  };

  useEffect(() => {
    fetchScans();
    fetchAlerts();
    fetchPriceComparisonSetting();
  }, []);

  const fetchScans = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/ted-ai-scans`, { headers });
      setScans(response.data);
    } catch (error) {
      toast.error('Failed to load scans');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      const response = await axios.get(`${API}/admin/price-alerts`, { headers });
      setAlerts(response.data);
    } catch (error) {
      console.error('Failed to load alerts');
    }
  };

  const handleDelete = async (scanId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      await axios.delete(`${API}/admin/ted-ai-scans/${scanId}`, { headers });
      setScans(scans.filter(s => s.id !== scanId));
      toast.success('Scan deleted');
    } catch (error) {
      toast.error('Failed to delete scan');
    }
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      const headers = getAuthHeader();
      if (!headers) return;
      await axios.delete(`${API}/admin/price-alerts/${alertId}`, { headers });
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success('Alert deleted');
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };

  const fetchPriceComparisonSetting = async () => {
    try {
      const response = await axios.get(`${API}/settings/price-comparison-enabled`);
      setPriceComparisonEnabled(response.data.enabled);
    } catch (error) {
      console.error('Error fetching price comparison setting:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0FA49C]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="ted-ai-scans-page">
      <div>
        <h2 className="text-xl font-bold text-[#222]">Ted AI Scans</h2>
        <p className="text-sm text-[#666]">{scans.length} scan{scans.length !== 1 ? 's' : ''} recorded</p>
      </div>

      {scans.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-200">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium text-[#666]">No scans yet</p>
          <p className="text-sm text-[#999]">Scans will appear here when customers use Ted AI</p>
        </div>
      ) : (
        <div className="space-y-3">
          {scans.map(scan => (
            <ScanCard key={scan.id} scan={scan} onDelete={handleDelete} onUpdate={(updated) => setScans(s => s.map(x => x.id === updated.id ? updated : x))} />
          ))}
        </div>
      )}

      {/* Price Alerts Section */}
      <div className="pt-4 border-t">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="w-5 h-5 text-[#F59E0B]" />
          <h2 className="text-xl font-bold text-[#222]">Price Alerts</h2>
          <span className="text-sm text-[#666] ml-1">{alerts.length} request{alerts.length !== 1 ? 's' : ''}</span>
        </div>

        {alerts.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
            <Bell className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm text-[#999]">No price alert requests yet</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map(alert => (
              <PriceAlertCard
                key={alert.id}
                alert={alert}
                onDelete={handleDeleteAlert}
                onUpdate={(updatedAlert) => {
                  setAlerts(alerts.map(a => a.id === updatedAlert.id ? { ...a, ...updatedAlert } : a));
                }}
                getAuthHeader={getAuthHeader}
                priceComparisonEnabled={priceComparisonEnabled}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
